import PageBackTitle from "../Component/PageBackTitle";
import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  MdOutlineModeEditOutline,
  MdDeleteOutline,
  MdExpandMore,
  MdAttachFile,
} from "react-icons/md";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import { HiOutlinePlus } from "react-icons/hi";
import { TbRefresh } from "react-icons/tb";
import Tooltip from "@mui/material/Tooltip";
import { IoClose } from "react-icons/io5";
import { IoCloseOutline } from "react-icons/io5";
import { FiUpload } from "react-icons/fi";
import axios from "axios";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import API_URL from "../Config.js";
import Pagination from "../Component/Pagination.js";
import useClickOutside from "../useClickOutside";
import "./expense.css";

const EXPENSE_CATEGORIES = ["Keshav Encon", "Client"];

/* ─────────────────────────────────────────────────────────────────────
   CUSTOM DATEPICKER HEADERS
───────────────────────────────────────────────────────────────────── */
const MonthYearHeader = ({
  date,
  decreaseYear,
  increaseYear,
  prevYearButtonDisabled,
  nextYearButtonDisabled,
}) => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      padding: "4px 8px",
    }}
  >
    <button
      onClick={decreaseYear}
      disabled={prevYearButtonDisabled}
      style={{
        background: "none",
        border: "none",
        cursor: prevYearButtonDisabled ? "not-allowed" : "pointer",
        fontSize: 16,
        padding: "2px 8px",
        color: prevYearButtonDisabled ? "#ccc" : "#444",
        borderRadius: 4,
      }}
    >
      {"<"}
    </button>
    <span style={{ fontWeight: 600, fontSize: 14 }}>{date.getFullYear()}</span>
    <button
      onClick={increaseYear}
      disabled={nextYearButtonDisabled}
      style={{
        background: "none",
        border: "none",
        cursor: nextYearButtonDisabled ? "not-allowed" : "pointer",
        fontSize: 16,
        padding: "2px 8px",
        color: nextYearButtonDisabled ? "#ccc" : "#444",
        borderRadius: 4,
      }}
    >
      {">"}
    </button>
  </div>
);

const FullDateHeader = ({
  date,
  decreaseMonth,
  increaseMonth,
  prevMonthButtonDisabled,
  nextMonthButtonDisabled,
}) => {
  const monthName = date.toLocaleString("en-IN", { month: "long" });
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "4px 8px",
      }}
    >
      <button
        onClick={decreaseMonth}
        disabled={prevMonthButtonDisabled}
        style={{
          background: "none",
          border: "none",
          cursor: prevMonthButtonDisabled ? "not-allowed" : "pointer",
          fontSize: 16,
          padding: "2px 8px",
          color: prevMonthButtonDisabled ? "#ccc" : "#444",
          borderRadius: 4,
        }}
      >
        {"<"}
      </button>
      <span style={{ fontWeight: 600, fontSize: 14 }}>
        {monthName} {date.getFullYear()}
      </span>
      <button
        onClick={increaseMonth}
        disabled={nextMonthButtonDisabled}
        style={{
          background: "none",
          border: "none",
          cursor: nextMonthButtonDisabled ? "not-allowed" : "pointer",
          fontSize: 16,
          padding: "2px 8px",
          color: nextMonthButtonDisabled ? "#ccc" : "#444",
          borderRadius: 4,
        }}
      >
        {">"}
      </button>
    </div>
  );
};

/* ── helpers ── */
const parseLocalDate = (str) => {
  if (!str) return null;
  const [y, m, d] = str.split("-").map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
};

const toDateStr = (date) => {
  if (!date) return "";
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

const fmtCur = (val) =>
  val != null
    ? `₹${parseFloat(val).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`
    : "—";

const fmtDate = (val) => {
  if (!val) return "—";
  try {
    return new Date(val).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return val;
  }
};

const emptyItem = () => ({
  _key: Date.now() + Math.random(),
  expense_date: "",
  amount: "",
  bill_receipt_no: "",
  purpose_description: "",
  vendor_remark: "",
  expense_category: EXPENSE_CATEGORIES[0],
});

const normalizeDateToYMD = (value) => {
  if (!value) return "";
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
  const d = new Date(value);
  if (isNaN(d.getTime())) return "";
  const year = d.getUTCFullYear();
  const month = String(d.getUTCMonth() + 1).padStart(2, "0");
  const day = String(d.getUTCDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

/* ─────────────────────────────────────────────────────────────────────
   SINGLE ITEM CARD — used inside Add popup
───────────────────────────────────────────────────────────────────── */
const ItemCard = ({
  item,
  index,
  showRemove,
  errors = {},
  onChange,
  onRemove,
}) => (
  <div className="exp-item-card exp-item-card--new">
    <div className="exp-item-card__header">
      <div className="exp-item-card__num">
        <span className="exp-item-card__num-badge">{index + 1}</span>
      </div>
      {showRemove && (
        <button
          type="button"
          className="exp-icon-btn exp-icon-btn--danger"
          onClick={() => onRemove(item._key)}
          title="Remove item"
        >
          <MdDeleteOutline size={15} />
        </button>
      )}
    </div>

    <div className="exp-item-card__body">
      <div className="exp-field-row exp-field-row--3">
        <div className="exp-field">
          <label className="exp-label">
            Amount (Rs.) <span className="exp-req">*</span>
          </label>
          <input
            type="number"
            placeholder="0.00"
            min="0"
            step="0.01"
            className={`exp-input ${errors[`${item._key}_amount`] ? "exp-input--err" : ""}`}
            value={item.amount}
            onChange={(e) => onChange(item._key, "amount", e.target.value)}
          />
          {errors[`${item._key}_amount`] && (
            <span className="exp-err-msg">{errors[`${item._key}_amount`]}</span>
          )}
        </div>

        <div className="exp-field">
          <label className="exp-label">Expense Date</label>
          <DatePicker
            selected={parseLocalDate(item.expense_date)}
            onChange={(date) =>
              onChange(item._key, "expense_date", toDateStr(date))
            }
            dateFormat="yyyy-MM-dd"
            placeholderText="YYYY-MM-DD"
            className="exp-input"
            renderCustomHeader={FullDateHeader}
          />
        </div>

        <div className="exp-field">
          <label className="exp-label">Category</label>
          <select
            className="exp-select"
            value={item.expense_category}
            onChange={(e) =>
              onChange(item._key, "expense_category", e.target.value)
            }
          >
            {EXPENSE_CATEGORIES.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="exp-field-row">
        <div className="exp-field">
          <label className="exp-label">Bill / Receipt No</label>
          <input
            type="text"
            placeholder="Receipt number"
            className="exp-input"
            value={item.bill_receipt_no}
            onChange={(e) =>
              onChange(item._key, "bill_receipt_no", e.target.value)
            }
          />
        </div>
        <div className="exp-field">
          <label className="exp-label">Vendor / Remark</label>
          <input
            type="text"
            placeholder="Vendor name or remark"
            className="exp-input"
            value={item.vendor_remark}
            onChange={(e) =>
              onChange(item._key, "vendor_remark", e.target.value)
            }
          />
        </div>
      </div>

      <div className="exp-field exp-field--full">
        <label className="exp-label">Purpose / Description</label>
        <textarea
          className="exp-textarea"
          rows={2}
          placeholder="Describe the purpose of this expense"
          value={item.purpose_description}
          onChange={(e) =>
            onChange(item._key, "purpose_description", e.target.value)
          }
        />
      </div>
    </div>
  </div>
);

/* ─────────────────────────────────────────────────────────────────────
   ADD ITEM POPUP — supports multiple items
───────────────────────────────────────────────────────────────────── */
const AddItemPopup = ({ onClose, onSuccess, showSnackbar, isAdmin }) => {
  const [saving, setSaving] = useState(false);
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(true);
  const [errors, setErrors] = useState({});
  const [selectedUserId, setSelectedUserId] = useState("");
  const [items, setItems] = useState([emptyItem()]);

  // Attachment states
  const [selectedAttachmentFiles, setSelectedAttachmentFiles] = useState([]);
  const [isAttachmentPopupOpen, setIsAttachmentPopupOpen] = useState(false);
  const attachmentFileInputRef = useRef(null);
  const attachmentPopupRef = useRef(null);
  const attachmentButtonRef = useRef(null);

  /* fetch users for admin */
  useEffect(() => {
    if (!isAdmin) {
      setUsersLoading(false);
      return;
    }
    (async () => {
      try {
        const authKey = localStorage.getItem("authKey");
        const res = await axios.get(`${API_URL}/api/get_user`, {
          headers: { Authorization: authKey },
          params: { per_page: 200 },
        });
        const d = res.data;
        setUsers(d.users || d.data || d || []);
      } catch {
        setUsers([]);
      } finally {
        setUsersLoading(false);
      }
    })();
  }, [isAdmin]);

  const handleItemChange = (key, field, value) => {
    setItems((prev) =>
      prev.map((it) => (it._key === key ? { ...it, [field]: value } : it)),
    );
    setErrors((prev) => ({ ...prev, [`${key}_${field}`]: "" }));
  };

  const handleAddItem = () => setItems((prev) => [...prev, emptyItem()]);

  const handleRemoveItem = (key) => {
    if (items.length === 1) return;
    setItems((prev) => prev.filter((it) => it._key !== key));
  };

  // Attachment handlers
  const handleUploadButtonClick = () => {
    attachmentFileInputRef.current?.click();
  };

  const handleFileInputChange = (e) => {
    const files = Array.from(e.target.files || []);
    setSelectedAttachmentFiles((prev) => [...prev, ...files]);
    e.target.value = "";
  };

  const handleAttachmentIconClick = () => {
    setIsAttachmentPopupOpen((prev) => !prev);
  };

  const handleCloseAttachmentPopup = () => {
    setIsAttachmentPopupOpen(false);
  };

  const handleDeleteAttachmentFile = (index) => {
    setSelectedAttachmentFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const validate = () => {
    const e = {};
    if (isAdmin && !selectedUserId) e.user_id = "Employee is required";
    items.forEach((it) => {
      if (!it.amount || isNaN(+it.amount) || +it.amount <= 0)
        e[`${it._key}_amount`] = "Required";
    });
    setErrors(e);
    return !Object.keys(e).length;
  };

  const handleSave = async () => {
    if (!validate()) {
      showSnackbar("Fix errors before saving", "error");
      return;
    }
    setSaving(true);
    try {
      const authKey = localStorage.getItem("authKey");

      // Use FormData to support file uploads
      const formData = new FormData();

      const itemsPayload = items.map(({ _key, ...rest }) => ({
        ...rest,
        amount: parseFloat(rest.amount),
        expense_date: normalizeDateToYMD(rest.expense_date) || undefined,
        bill_receipt_no: rest.bill_receipt_no || undefined,
        purpose_description: rest.purpose_description || undefined,
        vendor_remark: rest.vendor_remark || undefined,
      }));

      formData.append("items", JSON.stringify(itemsPayload));
      if (isAdmin && selectedUserId) formData.append("user_id", selectedUserId);

      selectedAttachmentFiles.forEach((file) => {
        formData.append("attachments_0", file);
      });

      const res = await axios.post(`${API_URL}/api/expense/items`, formData, {
        headers: {
          Authorization: authKey,
          "Content-Type": "multipart/form-data",
        },
      });

      if (res.status === 201) {
        showSnackbar(
          `${res.data.item_ids?.length || items.length} item(s) added successfully`,
        );
        onSuccess();
        onClose();
      }
    } catch (err) {
      showSnackbar(
        err.response?.data?.message || "Error adding items",
        "error",
      );
    } finally {
      setSaving(false);
    }
  };

  const parseLocalDateInner = (str) => {
    if (!str) return null;
    const normalized = normalizeDateToYMD(str);
    if (!normalized) return null;
    const [y, m, d] = normalized.split("-").map(Number);
    if (!y || !m || !d) return null;
    return new Date(y, m - 1, d);
  };

  const toDateStrInner = (date) => {
    if (!date) return "";
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  };

  return (
    <div className="exp-overlay">
      <div className="exp-modal exp-modal--lg">
        <div className="exp-modal__header">
          <div className="exp-modal__title-wrap">
            <div>
              <h4 className="exp-modal__title">Add Expense</h4>
              <p className="exp-modal__sub">
                Fill in expense details — add as many expenses as needed
              </p>
            </div>
          </div>
          <button className="exp-modal__close" onClick={onClose}>
            <IoClose size={18} />
          </button>
        </div>

        <div className="exp-modal__body exp-modal__body--scroll">
          {/* ── Employee selection — admin only, always at top ── */}
          {isAdmin && (
            <div style={{ marginBottom: 16 }}>
              <div className="exp-field">
                <label className="exp-label">
                  Employee <span className="exp-req">*</span>
                </label>
                <select
                  className={`exp-select ${errors.user_id ? "exp-input--err" : ""}`}
                  value={selectedUserId}
                  onChange={(e) => {
                    setSelectedUserId(e.target.value);
                    setErrors((p) => ({ ...p, user_id: "" }));
                  }}
                  disabled={usersLoading}
                >
                  <option value="">
                    {usersLoading ? "Loading…" : "Select employee"}
                  </option>
                  {users.map((u) => (
                    <option key={u.ID || u.id} value={u.ID || u.id}>
                      {u.Name || u.name}
                      {u.Designation ? ` — ${u.Designation}` : ""}
                    </option>
                  ))}
                </select>
                {errors.user_id && (
                  <span className="exp-err-msg">{errors.user_id}</span>
                )}
              </div>
            </div>
          )}

          {/* ── Item cards ── */}
          {items.map((item, idx) => (
            <ItemCard
              key={item._key}
              item={item}
              index={idx}
              showRemove={items.length > 1}
              errors={errors}
              onChange={handleItemChange}
              onRemove={handleRemoveItem}
            />
          ))}

          {/* ── Add another item button — below last card ── */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              marginTop: 12,
            }}
          >
            {/* ── Attachments ── */}
            <div className="form-group-attachments">
              <div className="exp-attach-div" style={{ margin: "0px 0px" }}>
                <label
                  className="exp-label"
                  style={{ fontSize: "11.5px", color: "#374151" }}
                >
                  Attachments
                </label>
                <button
                  type="button"
                  className="challan-btn-exp"
                  onClick={handleUploadButtonClick}
                  ref={attachmentButtonRef}
                  style={{width:"280px"}}
                >
                  <FiUpload /> Upload File
                </button>
                <input
                  type="file"
                  ref={attachmentFileInputRef}
                  onChange={handleFileInputChange}
                  style={{ display: "none" }}
                  multiple
                />
                {selectedAttachmentFiles.length > 0 && (
                  <span
                    className="exp-file-btn"
                    onClick={handleAttachmentIconClick}
                  >
                    <MdAttachFile className="inquiry-file-icon" />
                    {selectedAttachmentFiles.length}
                  </span>
                )}
                {isAttachmentPopupOpen && (
                  <div
                    className="exp-attach-popup-amc1"
                    ref={attachmentPopupRef}
                  >
                    <div className="attach-popup-contant">
                      <div className="attachment-header">
                        <div className="attachment-header-content">
                          Attachments
                        </div>
                        <button
                          className="fiile-close-btn"
                          onClick={handleCloseAttachmentPopup}
                        >
                          <IoCloseOutline className="file-close-icon" />
                        </button>
                      </div>
                      {selectedAttachmentFiles.length > 0 && (
                        <div className="attach-scrroll-body">
                          {selectedAttachmentFiles.map((file, index) => (
                            <div
                              className="attach-popup-contant-body"
                              key={index}
                            >
                              <div className="attach-file-container">
                                <div className="attach-file-show">
                                  <div className="text-attachment">
                                    {file.name}
                                  </div>
                                  <div className="text-attachment-file">
                                    File Size: ({(file.size / 1024).toFixed(2)}{" "}
                                    KB)
                                  </div>
                                </div>
                                <div
                                  className="file-delete-btn"
                                  onClick={() =>
                                    handleDeleteAttachmentFile(index)
                                  }
                                >
                                  <MdDeleteOutline className="file-delete-icon" />
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <button
              type="button"
              className="exp-add-row-btn"
              onClick={handleAddItem}
            >
              <HiOutlinePlus size={14} /> Add Another Expense
            </button>
          </div>
        </div>

        <div className="exp-modal__footer">
          <div style={{ fontSize: 12, color: "#888" }}>
            {items.length} item(s) to be added
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button className="exp-btn exp-btn--ghost" onClick={onClose}>
              Cancel
            </button>
            <button
              className="exp-btn exp-btn--primary"
              onClick={handleSave}
              disabled={saving}
            >
              {saving && <span className="exp-spinner" />}
              {saving ? "Saving…" : `Save ${items.length} Item(s)`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────────────
   EDIT ITEM POPUP
───────────────────────────────────────────────────────────────────── */
const EditItemPopup = ({
  item,
  onClose,
  onSuccess,
  showSnackbar,
  handleViewAttachment,
  handleDownloadAttachment,
}) => {
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [form, setForm] = useState({
    expense_date: normalizeDateToYMD(item.expense_date),
    amount: item.amount ?? "",
    bill_receipt_no: item.bill_receipt_no ?? "",
    purpose_description: item.purpose_description ?? "",
    vendor_remark: item.vendor_remark ?? "",
    expense_category: item.expense_category ?? EXPENSE_CATEGORIES[0],
  });

  // Attachment states
  const [selectedAttachmentFiles, setSelectedAttachmentFiles] = useState([]);
  const [isAttachmentPopupOpen, setIsAttachmentPopupOpen] = useState(false);
  const attachmentFileInputRef = useRef(null);
  const attachmentPopupRef = useRef(null);
  const attachmentButtonRef = useRef(null);

  // Existing server attachments + newly picked files combined for the badge count
  const existingAttachments = item.attachments || [];
  const totalAttachmentCount =
    existingAttachments.length + selectedAttachmentFiles.length;

  const handleChange = (field, value) => {
    setForm((p) => ({ ...p, [field]: value }));
    if (errors[field]) setErrors((p) => ({ ...p, [field]: "" }));
  };

  // Attachment handlers
  const handleUploadButtonClick = () => {
    attachmentFileInputRef.current?.click();
  };

  const handleFileInputChange = (e) => {
    const files = Array.from(e.target.files || []);
    setSelectedAttachmentFiles((prev) => [...prev, ...files]);
    e.target.value = "";
  };

  const handleAttachmentIconClick = () => {
    setIsAttachmentPopupOpen((prev) => !prev);
  };

  const handleCloseAttachmentPopup = () => {
    setIsAttachmentPopupOpen(false);
  };

  const handleDeleteAttachmentFile = (index) => {
    setSelectedAttachmentFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const validate = () => {
    const e = {};
    if (!form.amount || isNaN(+form.amount) || +form.amount <= 0)
      e.amount = "Valid amount is required";
    setErrors(e);
    return !Object.keys(e).length;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      const authKey = localStorage.getItem("authKey");

      if (selectedAttachmentFiles.length > 0) {
        // Use multipart/form-data when files are present
        const formData = new FormData();
        const dataPayload = {
          expense_date: normalizeDateToYMD(form.expense_date) || undefined,
          amount: parseFloat(form.amount),
          bill_receipt_no: form.bill_receipt_no,
          purpose_description: form.purpose_description,
          vendor_remark: form.vendor_remark,
          expense_category: form.expense_category,
        };
        formData.append("data", JSON.stringify(dataPayload));
        selectedAttachmentFiles.forEach((file) => {
          formData.append("attachments", file);
        });
        await axios.put(`${API_URL}/api/expense/items/${item.id}`, formData, {
          headers: {
            Authorization: authKey,
            "Content-Type": "multipart/form-data",
          },
        });
      } else {
        await axios.put(
          `${API_URL}/api/expense/items/${item.id}`,
          {
            expense_date: normalizeDateToYMD(form.expense_date) || undefined,
            amount: parseFloat(form.amount),
            bill_receipt_no: form.bill_receipt_no,
            purpose_description: form.purpose_description,
            vendor_remark: form.vendor_remark,
            expense_category: form.expense_category,
          },
          { headers: { Authorization: authKey } },
        );
      }

      showSnackbar("Item updated successfully");
      onSuccess();
      onClose();
    } catch (err) {
      showSnackbar(
        err.response?.data?.message || "Error updating item",
        "error",
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="exp-overlay">
      <div className="exp-modal exp-modal--lg">
        <div className="exp-modal__header">
          <div className="exp-modal__title-wrap">
            <div>
              <h4 className="exp-modal__title">Edit Expense Item</h4>
              <p className="exp-modal__sub">
                {item.employee_name || "—"} &nbsp;·&nbsp;{" "}
                {fmtDate(item.expense_date)}
              </p>
            </div>
          </div>
          <button className="exp-modal__close" onClick={onClose}>
            <IoClose size={18} />
          </button>
        </div>

        {/* Info strip */}
        <div className="exp-advance-strip">
          <div className="exp-advance-strip__item">
            <span className="exp-advance-strip__label">Employee</span>
            <span className="exp-advance-strip__value">
              {item.employee_name || "—"}
            </span>
          </div>
          <div className="exp-advance-strip__item">
            <span className="exp-advance-strip__label">Category</span>
            <span className="exp-advance-strip__value">
              {item.expense_category || "—"}
            </span>
          </div>
          <div className="exp-advance-strip__item">
            <span className="exp-advance-strip__label">Current Amount</span>
            <span className="exp-advance-strip__value exp-advance-strip__value--blue">
              {fmtCur(item.amount)}
            </span>
          </div>
        </div>

        <div className="exp-modal__body">
          <div className="exp-field-row exp-field-row--3">
            <div className="exp-field">
              <label className="exp-label">
                Amount (Rs.) <span className="exp-req">*</span>
              </label>
              <input
                type="number"
                min="0"
                step="0.01"
                className={`exp-input ${errors.amount ? "exp-input--err" : ""}`}
                value={form.amount}
                onChange={(e) => handleChange("amount", e.target.value)}
              />
              {errors.amount && (
                <span className="exp-err-msg">{errors.amount}</span>
              )}
            </div>

            <div className="exp-field">
              <label className="exp-label">Expense Date</label>
              <DatePicker
                selected={parseLocalDate(form.expense_date)}
                onChange={(date) =>
                  handleChange("expense_date", toDateStr(date))
                }
                dateFormat="yyyy-MM-dd"
                placeholderText="YYYY-MM-DD"
                className="exp-input"
                renderCustomHeader={FullDateHeader}
              />
            </div>

            <div className="exp-field">
              <label className="exp-label">Category</label>
              <select
                className="exp-select"
                value={form.expense_category}
                onChange={(e) =>
                  handleChange("expense_category", e.target.value)
                }
              >
                {EXPENSE_CATEGORIES.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="exp-field-row">
            <div className="exp-field">
              <label className="exp-label">Bill / Receipt No</label>
              <input
                type="text"
                className="exp-input"
                value={form.bill_receipt_no}
                onChange={(e) =>
                  handleChange("bill_receipt_no", e.target.value)
                }
              />
            </div>
            <div className="exp-field">
              <label className="exp-label">Vendor / Remark</label>
              <input
                type="text"
                className="exp-input"
                value={form.vendor_remark}
                onChange={(e) => handleChange("vendor_remark", e.target.value)}
              />
            </div>
          </div>

          <div className="exp-field exp-field--full">
            <label className="exp-label">Purpose / Description</label>
            <textarea
              className="exp-textarea"
              rows={2}
              value={form.purpose_description}
              onChange={(e) =>
                handleChange("purpose_description", e.target.value)
              }
            />
          </div>

          {/* ── Attachments ── */}
          <div className="form-group-attachments" style={{ marginTop: 12 }}>
            <div className="exp-attach-div">
              <label className="exp-edit-label" style={{ color: "#374151" }}>
                Attachments
              </label>
              <button
                type="button"
                className="challan-btn-exp-edit"
                onClick={handleUploadButtonClick}
                ref={attachmentButtonRef}
              >
                <FiUpload /> Upload File
              </button>
              <input
                type="file"
                ref={attachmentFileInputRef}
                onChange={handleFileInputChange}
                style={{ display: "none" }}
                multiple
              />

              {/* Blue icon — shows total count (existing server files + new picks) */}
              {totalAttachmentCount > 0 && (
                <span
                  className="exp-file-btn-edit"
                  onClick={handleAttachmentIconClick}
                >
                  <MdAttachFile className="inquiry-file-icon" />
                  {totalAttachmentCount}
                </span>
              )}

              {/* Popup — lists existing files (with View/Download) + new files (with Remove) */}
              {isAttachmentPopupOpen && (
                <div className="exp-attach-popup-amc1" ref={attachmentPopupRef}>
                  <div className="attach-popup-contant">
                    <div className="attachment-header">
                      <div className="attachment-header-content">
                        Attachments
                      </div>
                      <button
                        className="fiile-close-btn"
                        onClick={handleCloseAttachmentPopup}
                      >
                        <IoCloseOutline className="file-close-icon" />
                      </button>
                    </div>
                    <div className="attach-scrroll-body">
                      {/* Existing server attachments — View & Download */}
                      {existingAttachments.map((attachment, idx) => (
                        <div
                          className="attach-popup-contant-body"
                          key={`existing-${idx}`}
                        >
                          <div className="attach-file-container">
                            <div className="attach-file-show">
                              <div className="text-attachment">
                                {attachment.filename
                                  ? attachment.filename.length > 20
                                    ? `${attachment.filename.substring(0, 15)}...`
                                    : attachment.filename
                                  : "File"}
                              </div>
                              <div className="text-attachment-file">
                                File Size: {attachment.file_size || "NA"} KB
                              </div>
                            </div>
                            <div className="file-footer">
                              <button
                                type="button"
                                className="file-download-attachment file-txt"
                                onClick={() =>
                                  handleDownloadAttachment(attachment)
                                }
                              >
                                Download
                              </button>

                              <button
                                type="button"
                                className="file-download-attachment file-txt"
                                onClick={() => handleViewAttachment(attachment)}
                              >
                                View
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}

                      {/* Newly selected files — Remove only (not saved yet) */}
                      {selectedAttachmentFiles.map((file, index) => (
                        <div
                          className="attach-popup-contant-body"
                          key={`new-${index}`}
                        >
                          <div className="attach-file-container">
                            <div className="attach-file-show">
                              <div className="text-attachment">{file.name}</div>
                              <div className="text-attachment-file">
                                File Size: ({(file.size / 1024).toFixed(2)} KB)
                              </div>
                            </div>
                            <div
                              className="file-delete-btn"
                              onClick={() => handleDeleteAttachmentFile(index)}
                            >
                              <MdDeleteOutline className="file-delete-icon" />
                            </div>
                          </div>
                        </div>
                      ))}

                      {/* Empty state — only shown if somehow popup opened with 0 total */}
                      {totalAttachmentCount === 0 && (
                        <div className="attach-popup-contant-body">
                          <div
                            className="text-attachment"
                            style={{ padding: "8px 0", color: "#888" }}
                          >
                            No attachments found
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="exp-modal__footer">
          <button className="exp-btn exp-btn--ghost" onClick={onClose}>
            Cancel
          </button>
          <button
            className="exp-btn exp-btn--primary"
            onClick={handleSave}
            disabled={saving}
          >
            {saving && <span className="exp-spinner" />}
            {saving ? "Updating…" : "Update Item"}
          </button>
        </div>
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────────────
   MAIN — Expense Items List
───────────────────────────────────────────────────────────────────── */
function ExpenseItems({ isSidebarExpanded }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);

  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  const [selectedRowIndex, setSelectedRowIndex] = useState(null);
  const [filterMonth, setFilterMonth] = useState("");

  const [isAddOpen, setIsAddOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const [page, setPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);
  const [totalItems, setTotalItems] = useState(0);

  // Attachment popup per row
  const [openAttachmentRowIndex, setOpenAttachmentRowIndex] = useState(null);

  const fetchTimeout = useRef(null);
  const containerRef = useRef(null);
  const popupRef = useRef(null);
  const buttonRef = useRef(null);
  const attachmentPopupRef = useRef(null);
  const attachmentButtonRef = useRef(null);

  useClickOutside(
    [containerRef, buttonRef],
    () => setSelectedRowIndex(null),
    200,
  );
  useClickOutside([popupRef, buttonRef], () => setSelectedRowIndex(null), 300);
  useClickOutside(
    [attachmentPopupRef, attachmentButtonRef],
    () => setOpenAttachmentRowIndex(null),
    200,
  );

  /* detect role */
  useEffect(() => {
    try {
      const role = localStorage.getItem("user_role") || "";
      setIsAdmin(["admin", "O & M Head"].includes(role));
    } catch {
      setIsAdmin(false);
    }
  }, []);

  const showSnackbar = (msg, sev = "success") => {
    setSnackbarMessage(msg);
    setSnackbarSeverity(sev);
    setSnackbarOpen(true);
  };

  const fetchData = useCallback(
    async (cp = page, pp = itemsPerPage, m = filterMonth) => {
      try {
        setLoading(true);
        const authKey = localStorage.getItem("authKey");
        const params = { page: cp, per_page: pp };
        if (m) params.report_month = m;
        const res = await axios.get(`${API_URL}/api/expense/items`, {
          headers: { Authorization: authKey },
          params,
        });
        const data = res.data;
        setItems(Array.isArray(data.items) ? data.items : []);
        setTotalItems(data.total ?? 0);
      } catch {
        showSnackbar("Failed to fetch expenses", "error");
      } finally {
        setLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  useEffect(() => {
    clearTimeout(fetchTimeout.current);
    fetchTimeout.current = setTimeout(
      () => fetchData(page, itemsPerPage, filterMonth),
      300,
    );
    return () => clearTimeout(fetchTimeout.current);
  }, [page, itemsPerPage, filterMonth, fetchData]);

  const handleViewAttachment = async (attachment) => {
    try {
      const authKey = localStorage.getItem("authKey");
      const attachmentId = attachment?.file_id;

      if (!attachmentId) {
        showSnackbar("Attachment id not found", "error");
        return;
      }

      const viewUrl = `${API_URL}/api/expense/attachments/${attachmentId}/view`;

      const response = await axios.get(viewUrl, {
        headers: {
          Authorization: authKey,
        },
        responseType: "blob",
      });

      const contentType =
        response.headers["content-type"] || "application/octet-stream";

      const blob = new Blob([response.data], { type: contentType });
      const fileURL = window.URL.createObjectURL(blob);

      const newWindow = window.open("", "_blank");

      if (!newWindow) {
        showSnackbar("Popup blocked. Please allow popups.", "error");
        window.URL.revokeObjectURL(fileURL);
        return;
      }

      if (contentType.startsWith("image/")) {
        newWindow.document.write(`
        <!DOCTYPE html>
        <html>
          <head>
            <title>Image Preview</title>
            <style>
              body {
                margin: 0;
                padding: 20px;
                display: flex;
                justify-content: center;
                align-items: center;
                min-height: 100vh;
                background-color: #f0f0f0;
              }
              img {
                max-width: 100%;
                max-height: 100vh;
                object-fit: contain;
                box-shadow: 0 4px 8px rgba(0,0,0,0.1);
              }
            </style>
          </head>
          <body>
            <img src="${fileURL}" alt="Attachment Preview" />
          </body>
        </html>
      `);
      } else if (contentType === "application/pdf") {
        newWindow.document.write(`
        <!DOCTYPE html>
        <html>
          <head>
            <title>PDF Preview</title>
            <style>
              body { margin: 0; padding: 0; }
              iframe { width: 100%; height: 100vh; border: none; }
            </style>
          </head>
          <body>
            <iframe src="${fileURL}" type="application/pdf"></iframe>
          </body>
        </html>
      `);
      } else if (contentType.startsWith("text/")) {
        const text = await new Response(response.data).text();

        newWindow.document.write(`
        <!DOCTYPE html>
        <html>
          <head>
            <title>Text Preview</title>
            <style>
              body {
                font-family: monospace;
                padding: 20px;
                background-color: #f8f8f8;
                line-height: 1.4;
              }
              pre {
                white-space: pre-wrap;
                word-wrap: break-word;
              }
            </style>
          </head>
          <body>
            <pre>${text
              .replace(/&/g, "&amp;")
              .replace(/</g, "&lt;")
              .replace(/>/g, "&gt;")}</pre>
          </body>
        </html>
      `);
      } else {
        newWindow.document.write(`
        <!DOCTYPE html>
        <html>
          <head>
            <title>File Preview</title>
            <style>
              body {
                font-family: Arial, sans-serif;
                padding: 20px;
                text-align: center;
                background-color: #f0f0f0;
              }
              .container {
                max-width: 400px;
                margin: 50px auto;
                padding: 30px;
                background: white;
                border-radius: 8px;
                box-shadow: 0 2px 10px rgba(0,0,0,0.1);
              }
              .download-btn {
                background-color: #007bff;
                color: white;
                padding: 10px 20px;
                border: none;
                border-radius: 4px;
                cursor: pointer;
                text-decoration: none;
                display: inline-block;
                margin-top: 20px;
              }
              .download-btn:hover {
                background-color: #0056b3;
              }
            </style>
          </head>
          <body>
            <div class="container">
              <h3>File Preview</h3>
              <p>This file type cannot be previewed directly.</p>
              <p>Content Type: ${contentType}</p>
              <a href="${fileURL}" download="${attachment?.filename || "attachment"}" class="download-btn">Download File</a>
            </div>
          </body>
        </html>
      `);
      }

      newWindow.document.close();

      setTimeout(() => {
        window.URL.revokeObjectURL(fileURL);
      }, 10000);
    } catch (error) {
      console.error("Error viewing attachment:", error);
      showSnackbar("Failed to view attachment", "error");
    }
  };

  const handleDownloadAttachment = async (attachment) => {
    try {
      const authKey = localStorage.getItem("authKey");
      const attachmentId = attachment?.file_id;
      const fileName = attachment?.filename || "attachment";

      if (!attachmentId) {
        showSnackbar("Attachment id not found", "error");
        return;
      }

      const response = await axios.get(
        `${API_URL}/api/expense/attachments/${attachmentId}/download`,
        {
          headers: { Authorization: authKey },
          responseType: "blob",
        },
      );

      const blob = new Blob([response.data]);
      const link = document.createElement("a");
      link.href = window.URL.createObjectURL(blob);
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(link.href);

      showSnackbar("Attachment downloaded successfully");
    } catch (error) {
      console.error("Error downloading attachment:", error);
      showSnackbar("Error downloading attachment", "error");
    }
  };

  const handlePaginationChange = (p, pp) => {
    setPage(p);
    setItemsPerPage(pp);
  };

  const handleRefresh = () => {
    setFilterMonth("");
    setPage(1);
    fetchData(1, itemsPerPage, "");
  };

  const handleButtonClick = (i, e) => {
    e.stopPropagation();
    setSelectedRowIndex(i === selectedRowIndex ? null : i);
  };

  const handleDelete = async (itemId) => {
    if (!window.confirm("Delete this expense item?")) return;
    setDeletingId(itemId);
    try {
      const authKey = localStorage.getItem("authKey");
      await axios.delete(`${API_URL}/api/expense/items/${itemId}`, {
        headers: { Authorization: authKey },
      });
      showSnackbar("Item deleted successfully");
      fetchData(page, itemsPerPage, filterMonth);
    } catch (err) {
      showSnackbar(
        err.response?.data?.message || "Error deleting item",
        "error",
      );
    } finally {
      setDeletingId(null);
      setSelectedRowIndex(null);
    }
  };

  const handleAttachmentIconClick = (e, index) => {
    e.stopPropagation();
    setOpenAttachmentRowIndex(openAttachmentRowIndex === index ? null : index);
  };

  const handleCloseAttachmentPopup = () => {
    setOpenAttachmentRowIndex(null);
  };

  const refreshAll = () => fetchData(page, itemsPerPage, filterMonth);

  return (
    <>
      <div
        className={`data-list-content-container page-content ${isSidebarExpanded ? "expanded" : "collapsed"}`}
      >
        <div
          className="data-list-content"
          ref={containerRef}
          style={{ position: "relative" }}
        >
          {/* ── Header ── */}
          <div className="data-list-header">
            <div className="data-list-header-content">
              <PageBackTitle>Expense</PageBackTitle>
              <div className="data-list-heading-content-right-side">
                <div
                  style={{
                    display: "flex",
                    gap: 8,
                    alignItems: "center",
                    width: "max-content",
                  }}
                >
                  <DatePicker
                    selected={filterMonth ? new Date(filterMonth) : null}
                    onChange={(date) => {
                      setFilterMonth(
                        date ? date.toISOString().slice(0, 7) : "",
                      );
                      setPage(1);
                    }}
                    dateFormat="MMM yyyy"
                    showMonthYearPicker
                    placeholderText="All months"
                    isClearable
                    className="filter-dropdown"
                    renderCustomHeader={MonthYearHeader}
                  />
                  <Tooltip title="Add Expense" arrow>
                    <button
                      className="new-button"
                      onClick={() => setIsAddOpen(true)}
                      style={{ width: "122px" }}
                    >
                      <HiOutlinePlus className="new-plus-icon" /> New
                    </button>
                  </Tooltip>
                  <Tooltip title="Refresh" arrow>
                    <button className="refresh-button" onClick={handleRefresh}>
                      <TbRefresh className="refresh-icon" />
                    </button>
                  </Tooltip>
                </div>
              </div>
            </div>
          </div>

          {/* ── Table ── */}
          <div
            className="data-list-body"
            onClick={() => {
              setSelectedRowIndex(null);
              setOpenAttachmentRowIndex(null);
            }}
            style={{ height: "86.6vh" }}
          >
            <div className="data-list-scroll-body">
              <div className="data-list-table">
                {loading ? (
                  <div className="loader-container">
                    <div className="loader" />
                    <div>Loading…</div>
                  </div>
                ) : items.length > 0 ? (
                  <table className="table multilevel-data">
                    <thead>
                      <tr>
                        <th className="center-alignment">SR. NO.</th>
                        {isAdmin && <th>EMPLOYEE</th>}
                        <th>DATE</th>
                        <th>AMOUNT</th>
                        <th>CATEGORY</th>
                        <th>BILL / RECEIPT</th>
                        <th>PURPOSE / DESCRIPTION</th>
                        <th>VENDOR / REMARK</th>
                        <th>ATTACHMENT</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody className="data-list-scroll-bodys">
                      {items.map((item, index) => (
                        <tr key={item.id} className="hoverable-row">
                          <td className="center-alignment">
                            {(page - 1) * itemsPerPage + index + 1}
                          </td>
                          {isAdmin && (
                            <td style={{ fontWeight: 500 }}>
                              {item.employee_name || "—"}
                            </td>
                          )}
                          <td>{fmtDate(item.expense_date)}</td>
                          <td>
                            <span className="exp-amount-cell rpt-cell--danger">
                              {fmtCur(item.amount)}
                            </span>
                          </td>
                          <td>
                            <span className="exp-cat-chip">
                              {item.expense_category || "General"}
                            </span>
                          </td>
                          <td>{item.bill_receipt_no || "—"}</td>
                          <td
                            style={{
                              maxWidth: 200,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                            title={item.purpose_description || ""}
                          >
                            {item.purpose_description || "—"}
                          </td>
                          <td className="rpt-cell--muted">
                            {item.vendor_remark || "—"}
                          </td>

                          {/* ── Attachment column ── */}
                          <td className="center-alignment">
                            {item.attachments && item.attachments.length > 0 ? (
                              <>
                                <span
                                  className="inquiry-file-btns"
                                  onClick={(e) =>
                                    handleAttachmentIconClick(e, index)
                                  }
                                  ref={
                                    openAttachmentRowIndex === index
                                      ? attachmentButtonRef
                                      : null
                                  }
                                >
                                  <MdAttachFile className="inquiry-file-icon" />
                                </span>

                                {openAttachmentRowIndex === index && (
                                  <div
                                    className="inquiry-attach-popup prRequest-popup"
                                    ref={attachmentPopupRef}
                                  >
                                    <div className="attach-popup-contant">
                                      <div className="attachment-header">
                                        <div className="attachment-header-content">
                                          Attachments
                                        </div>
                                        <button
                                          className="fiile-close-btn"
                                          onClick={handleCloseAttachmentPopup}
                                        >
                                          <IoCloseOutline className="file-close-icon" />
                                        </button>
                                      </div>

                                      <div className="attach-popup-contant-body">
                                        <div className="inquiry-attach-file-containerssss">
                                          <div className="attach-file-showsss">
                                            <div className="text-attachmentss">
                                              <div className="attach-popup-content-body">
                                                {item.attachments.map(
                                                  (
                                                    attachment,
                                                    attachmentIndex,
                                                  ) => (
                                                    <div
                                                      className="inquiry-attach-file-container"
                                                      key={attachmentIndex}
                                                    >
                                                      <div className="inquiry-attacth-file-model">
                                                        <div className="attach-file-show">
                                                          <div className="text-attachment">
                                                            {attachment.filename
                                                              ? attachment
                                                                  .filename
                                                                  .length > 20
                                                                ? `${attachment.filename.substring(0, 15)}...`
                                                                : attachment.filename
                                                              : "File"}
                                                          </div>

                                                          <div className="text-attachment-file">
                                                            File Size:{" "}
                                                            {attachment.file_size ||
                                                              "NA"}{" "}
                                                            KB
                                                          </div>
                                                        </div>

                                                        <div className="file-footer">
                                                          <button
                                                            type="button"
                                                            className="file-download-attachment file-txt"
                                                            onClick={() =>
                                                              handleDownloadAttachment(
                                                                attachment,
                                                              )
                                                            }
                                                          >
                                                            Download
                                                          </button>

                                                          <button
                                                            type="button"
                                                            className="file-download-attachment file-txt"
                                                            onClick={() =>
                                                              handleViewAttachment(
                                                                attachment,
                                                              )
                                                            }
                                                          >
                                                            View
                                                          </button>
                                                        </div>
                                                      </div>
                                                    </div>
                                                  ),
                                                )}
                                              </div>
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                )}
                              </>
                            ) : (
                              "-"
                            )}
                          </td>

                          <td className="edit-pop">
                            <button
                              className="edit-data-button"
                              onClick={(e) => handleButtonClick(index, e)}
                            >
                              <div className="edit-button">
                                <MdExpandMore />
                              </div>
                            </button>
                            {selectedRowIndex === index && (
                              <div className="edit-popup" ref={popupRef}>
                                <div className="edit-popup-contant">
                                  <div className="edit-popup-button">
                                    <button
                                      className="edit-button-inquiry"
                                      onClick={() => {
                                        setEditItem(item);
                                        setSelectedRowIndex(null);
                                      }}
                                    >
                                      <MdOutlineModeEditOutline /> Edit
                                    </button>
                                  </div>
                                </div>
                              </div>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div className="no-users-container">
                    <div className="no-users-message">
                      No expense found. Click <strong>New</strong> to add one.
                    </div>
                  </div>
                )}
              </div>
              <Pagination
                showPerPage={itemsPerPage}
                total={totalItems}
                onPaginationChange={handlePaginationChange}
              />
            </div>
          </div>
        </div>
      </div>

      {/* ── Popups ── */}
      {isAddOpen && (
        <AddItemPopup
          onClose={() => setIsAddOpen(false)}
          onSuccess={refreshAll}
          showSnackbar={showSnackbar}
          isAdmin={isAdmin}
        />
      )}
      {editItem && (
        <EditItemPopup
          item={editItem}
          onClose={() => setEditItem(null)}
          onSuccess={refreshAll}
          showSnackbar={showSnackbar}
          handleViewAttachment={handleViewAttachment}
          handleDownloadAttachment={handleDownloadAttachment}
        />
      )}

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={(e, r) => {
          if (r !== "clickaway") setSnackbarOpen(false);
        }}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        style={{ marginTop: "35px" }}
      >
        <MuiAlert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
        >
          {snackbarMessage}
        </MuiAlert>
      </Snackbar>
    </>
  );
}

export default ExpenseItems;
